import React from 'react';
import { Accordion, Stack, Select, Switch, Paper, Text, Group, Badge, TextInput } from '@mantine/core';
import type { DeviceConfig, PanelConfig } from '../../data/DeviceConfig';
import PanelConfigEditor from './PanelConfigEditor';
import { CLOUD_UI_PLUGIN_ID, PLUGIN_IDS } from './componentRegistry';

interface VisualConfigEditorProps {
    config: DeviceConfig;
    onChange: (config: DeviceConfig) => void;
}

const getPanelValue = (panel: PanelConfig | null): PanelConfig => ({
    commercialTakeover: panel?.commercialTakeover ?? false,
    content: panel?.content ?? [],
    commercialContent: panel?.commercialContent ?? [],
});

const VisualConfigEditor: React.FC<VisualConfigEditorProps> = ({ config, onChange }) => {
    const handlePanelChange = (
        key: 'topPanel' | 'middlePanel' | 'bottomPanel',
        panel: PanelConfig
    ) => {
        onChange({ ...config, [key]: panel });
    };

    // If the current pluginId isn't in the list, add it as a custom option
    const pluginData =
        config.pluginId && !PLUGIN_IDS.find(p => p.value === config.pluginId)
            ? [...PLUGIN_IDS, { value: config.pluginId, label: config.pluginId }]
            : PLUGIN_IDS;

    const panelItemCount = (panel: PanelConfig | null) =>
        (panel?.content?.length ?? 0) + (panel?.commercialContent?.length ?? 0);

    return (
        <Stack gap="md">
            {/* Plugin-level settings */}
            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                        Plugin Settings
                    </Text>
                    <Select
                        label="Plugin"
                        data={pluginData}
                        value={config.pluginId || null}
                        onChange={value =>
                            onChange({
                                ...config,
                                pluginId: value || '',
                            })
                        }
                        placeholder="Select a plugin"
                        allowDeselect={false}
                    />
                    {config.pluginId === CLOUD_UI_PLUGIN_ID ? (
                        <TextInput
                            label="URL"
                            description="HTTP or HTTPS iframe URL for Cloud UI."
                            placeholder="https://"
                            value={config.iframeUri ?? ''}
                            onChange={e =>
                                onChange({
                                    ...config,
                                    iframeUri: e.currentTarget.value,
                                })
                            }
                        />
                    ) : (
                        <>
                            <Switch
                                label="Debug Mode"
                                description="**Only enable for short periods of time. Sends every frame that's processed from the main screen to the server."
                                checked={!!config.isDebug}
                                onChange={e => onChange({ ...config, isDebug: e.currentTarget.checked })}
                            />
                        </>
                    )}
                </Stack>
            </Paper>

            {/* Panel accordions — hidden for Cloud UI (values stay in config for when user switches back) */}
            {config.pluginId !== CLOUD_UI_PLUGIN_ID && (
                <Accordion
                    multiple
                    defaultValue={['topPanel', 'middlePanel', 'bottomPanel']}
                    variant="separated"
                >
                {(
                    [
                        { key: 'topPanel', label: 'Top Panel' },
                        { key: 'middlePanel', label: 'Middle Panel' },
                        { key: 'bottomPanel', label: 'Bottom Panel' },
                    ] as const
                ).map(({ key, label }) => (
                    <Accordion.Item key={key} value={key}>
                        <Accordion.Control>
                            <Group gap="xs">
                                <Text fw={500}>{label}</Text>
                                {panelItemCount(config[key]) > 0 && (
                                    <Badge size="sm" variant="light" color="violet">
                                        {panelItemCount(config[key])} items
                                    </Badge>
                                )}
                            </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <PanelConfigEditor
                                panel={getPanelValue(config[key])}
                                onChange={panel => handlePanelChange(key, panel)}
                            />
                        </Accordion.Panel>
                    </Accordion.Item>
                ))}
                </Accordion>
            )}
        </Stack>
    );
};

export default VisualConfigEditor;
